import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";

// components
import { LoaderMUI } from "../assets/global/loader/loader-mui";
import { AlertMessage } from "../assets/global/alert/alert-message";
import { ResultsCompare } from "../components/results/comparison/results-compare";
import { SurveyWrapper } from "../components/results/comparison/survey";
import { MessageScreen } from "../components/layout/message-screen";

// context
import { useAppContext } from "../services/context/app-context";

// api
import { fetchProperty } from "../services/api/site-settings-api";
import { fetchRunners } from "../services/api/results-api";
import { axiosInstance } from "../services/api/api";
import {
  fetchBuildings,
  fetchBuildingTypes,
  fetchConnectionTypes,
} from "../services/api/planner-api";
import { apiGetRandomResults } from "../services/api/survey.api";

// context
import { useResultsContext } from "../services/context/results-context";
import { useUserContext } from "../services/context/user-context";

// hooks
import { useWebSocket } from "../hooks/useWebSocket";


export const ResultsSurveyPage = () => {
  const { projectId } = useParams();
  const { userData, surveyTallyId } = useUserContext();
  const { setRunners, setBuildings, setBuildingsTypes, setConnectionTypes } =
    useResultsContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [propertyResponse, setPropertyResponse] = useState(null);

  const [leftResult, setLeftResult] = useState({ result: null, runner: null });
  const [rightResult, setRightResult] = useState({
    result: null,
    runner: null,
  });

  const joinDataId = useMemo(() => {
    if (!projectId || !userData?.userId) return "";
    return `${projectId}:${userData.userId}`;
  }, [projectId, userData?.userId]);

  const fetchData = async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const [
        fetchedBuildingTypes,
        fetchedConnectionTypes,
        fetchedRandomResults,
      ] = await Promise.all([
        fetchBuildingTypes(),
        fetchConnectionTypes(),
        apiGetRandomResults(projectId),
      ]);

      if (!fetchedRandomResults?.length) {
        throw new Error("No random results found");
      }

      if (fetchedRandomResults.length > 1) {
        setLeftResult(fetchedRandomResults[0]);
        setRightResult(fetchedRandomResults[1]);
      }

      setBuildingsTypes(fetchedBuildingTypes);
      setConnectionTypes(fetchedConnectionTypes);
    } catch (err) {
      console.error("Error fetching comparison data:", err);
      const message =
        err.response?.data?.message || err.message || "Error fetching data";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const [wsReady, setWsReady] = useState(false);

  const onMessageHandler = useCallback(
    (raw) => {
      let msg;
      try {
        msg = JSON.parse(raw);
      } catch {
        return;
      }

      if (msg.event === "surveyJoined") {
        if (msg.data === joinDataId) {
          setWsReady(true);
        }
        return;
      }

      if (msg.event === "surveyResponse") {
        // user submitted survey
        fetchData();
        return;
      }
    },
    [joinDataId],
  );

  const { status, readyState, lastError } = useWebSocket({
    joinEvent: "joinSurvey",
    joinData: joinDataId,
    joinServerEvent: "surveyJoined",
    onMessage: onMessageHandler,
  });

  const onSkipHandler = () => {
    fetchData();
  }

  if (loading || !wsReady) return <LoaderMUI withBackdrop={true} />;
  /* if (error) return <AlertMessage message={error} type="error" />; */

  if (
    error ||
    !surveyTallyId ||
    !leftResult.result ||
    !rightResult.result ||
    !leftResult.runner ||
    !rightResult.runner
  )
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          padding: "40px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MessageScreen
          msg="No survey or valid results were found for this project. Please refresh the page and make sure the project has more than one result."
          title="Survey or Results Not Found"
        />
      </Box>
    );

  if (readyState === "failed") {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          p: "40px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MessageScreen
          title="Live updates unavailable"
          msg={`We couldn't subscribe to survey updates. ${lastError ?? ""}`}
        />
      </Box>
    );
  }

  return (
    <SurveyWrapper
      projectId={projectId}
      leftResult={leftResult}
      rightResult={rightResult}
      onSkipHandler={onSkipHandler}
    />
  );
};
